import type { BlogPost } from '@/types'

import { useRef } from 'react'
import Link from 'next/link'

import { gsap, registerGsap, wfEase, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface BlogGridProps {
    posts: BlogPost[]
}

function BlogCard({ post }: { post: BlogPost }) {
    const ref = useRef<HTMLDivElement>(null)

    // a-38 "Fade In Slow": each post fades in as it scrolls into view.
    useIsomorphicLayoutEffect(() => {
        const el = ref.current
        if (!el) return
        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { opacity: 1, y: 0 })
            return
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1.1,
                    ease: wfEase('ease'),
                    scrollTrigger: { trigger: el, start: 'top 88%', once: true }
                }
            )
        }, ref)

        return () => ctx.revert()
    }, [])

    return (
        <div
            ref={ref}
            role='listitem'
            className='post-collection-item w-dyn-item'
            style={{ opacity: 0 }}
        >
            <Link
                href={`/blog/${post.slug}`}
                className='post-item w-inline-block'
                data-cursor='big'
            >
                <div className='post-item-image-container'>
                    <img
                        loading='lazy'
                        alt={post.name}
                        src={post.thumb || post.main}
                        className='post-item-image'
                    />
                </div>
                <h3 className='post-item-title'>{post.name}</h3>
                <div className='post-item-content-bottom'>
                    <div className='blog-item-category'>{post.category}</div>
                </div>
            </Link>
        </div>
    )
}

export function BlogGrid({ posts }: BlogGridProps) {
    return (
        <div className='w-dyn-list'>
            <div role='list' className='blog-post-grid w-dyn-items'>
                {posts.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                ))}
            </div>
        </div>
    )
}
